import { inject, Injectable } from "@angular/core";
import { EntityFacadeService } from "../../../../shared/store/entity-store/entity-facade.service";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { Checklist } from "../../models/checklist.model";
import { ChecklistApiService } from "./checklist-api.service";
import {
  ChecklistState,
  ChecklistStoreService,
} from "./checklist-store.service";
import {
  Untrack,
  UpdateStatus,
} from "../../../../shared/store/utils/store-utils.service";
import { firstValueFrom } from "rxjs";
import { Poste } from "app/features/production/models/poste.model";

@Injectable({ providedIn: "root" })
export class ChecklistFacadeService extends EntityFacadeService<
  Checklist,
  ChecklistApiService,
  ChecklistState,
  EntityMetadata,
  ChecklistStoreService
> {
  constructor() {
    const api = inject(ChecklistApiService);
    const store = inject(ChecklistStoreService);
    super(api, store);
    store.initStatus(ChecklistFacadeService);
  }

  getByListId(listId: string) {
    return this.elements()
      .filter((e) => e().listId === listId && !e().parent)
      .map((e) => e())
      .sort((a, b) => a.index - b.index);
  }

  getByBundleId(bundleId: string) {
    return this.elements()
      .filter((e) => e().bundleId === bundleId && !!e().parent)
      .map((e) => e())
      .sort((a, b) => a.index - b.index);
  }

  getByOfId(ofId: string) {
    return this.elements()
      .filter((e) => e().ofId === ofId)
      .map((e) => e())
      .sort((a, b) => a.index - b.index);
  }

  @Untrack
  @UpdateStatus
  async loadByListId(listId: string): Promise<void> {
    this.store.incrementLoading();
    try {
      this.store.addMany(await firstValueFrom(this.api.loadByListId(listId)));
      this.store.decrementLoading();
    } catch (e) {
      this.store.decrementLoading();
      this.store.patchState({
        error: e,
      });
      throw e;
    }
  }

  @Untrack
  @UpdateStatus
  async loadChecklistForOf(ofId: string): Promise<void> {
    this.store.incrementLoading();
    try {
      this.store.addMany(
        await firstValueFrom(this.api.loadChecklistsForOf(ofId)),
      );
      this.store.decrementLoading();
    } catch (e) {
      this.store.decrementLoading();
      this.store.patchState({
        error: e,
      });
      throw e;
    }
  }

  @Untrack
  @UpdateStatus
  async duplicate(cl: Checklist): Promise<void> {
    await this.update(cl, this.api.duplicate);
  }

  @Untrack
  @UpdateStatus
  async saveAll(checklists: Checklist[]): Promise<void> {
    this.store.addMany(await firstValueFrom(this.api.saveAll(checklists)));
  }

  @Untrack
  @UpdateStatus
  async loadBundleFor(listId: string, date: string, dateFin?: string) {
    const bundle = await firstValueFrom(
      this.api.loadBundleFor(listId, date, dateFin),
    );

    for (const checklist of bundle.checklists) {
      this.store.upsertOne(checklist);
    }

    this.store.patchState({ currentBundle: bundle });
  }

  @Untrack
  @UpdateStatus
  async loadByBundleId(bundleId: string) {
    const bundle = await firstValueFrom(this.api.loadBundleById(bundleId));
    this.store.patchState({ currentBundle: bundle });
  }

  @Untrack
  @UpdateStatus
  async commitChecklist(checklist: Checklist): Promise<void> {
    const bundle = await firstValueFrom(this.api.commit(checklist));
    this.store.patchState({ currentBundle: bundle });
  }

  @Untrack
  @UpdateStatus
  async deleteDoneChecklist(checklistId: string): Promise<void> {
    const bundle = await firstValueFrom(this.api.deleteDone(checklistId));
    this.store.removeOne(checklistId);
    this.store.patchState({ currentBundle: bundle });
  }

  @Untrack
  @UpdateStatus
  async buildChecklistForOpe(
    checklist: Checklist,
    ope: Poste,
  ): Promise<Checklist> {
    const saved = await firstValueFrom(
      this.api.buildChecklistForOpe(checklist, ope),
    );
    this.store.upsertOne(saved);
    return saved;
  }

  @Untrack
  @UpdateStatus
  async buildChecklist(checklist: Checklist): Promise<Checklist> {
    const saved = await firstValueFrom(this.api.buildChecklist(checklist));
    this.store.upsertOne(saved);
    return saved;
  }
}
